import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Button, InputField, RadioInput, UploadInput } from "@/components";
import { IoArrowForward } from "react-icons/io5";
import { bankDetailsSchema } from "../../../schemas";
import { useFieldError, useValidation } from "@/";

const BankDetails = () => {
  const {
    handleNextStep,
    handlePreviousStep,
    formData,
    setFormData,
    isDisabled,
  } = useOutletContext();
  const { validate, errors } = useValidation(bankDetailsSchema);
  const [formErrors, setFormErrors] = useState(errors);
  const getErrorMessage = useFieldError(formErrors || []);

  // Local state only for confirmAccountNumber, used for validation
  // Initialize from formData if it exists
  const [confirmAccountNumber, setConfirmAccountNumber] = useState(
    formData.bankDetails.confirmAccountNumber || ""
  );

  // Helper function to update bankDetails in parent's state
  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      bankDetails: {
        ...prev.bankDetails,
        [field]: value,
      },
    }));
  };

  // Update confirmAccountNumber in both local state and parent formData
  const handleConfirmAccountNumberChange = (value) => {
    setConfirmAccountNumber(value);
    handleChange("confirmAccountNumber", value);
  };

  const previous = () => {
    handlePreviousStep();
  };

  const handleNext = () => {
    if (isDisabled) {
      handleNextStep();
      return;
    }
    setFormErrors([]);
    const result = validate({
      ...formData.bankDetails,
      confirmAccountNumber,
    });

    if (!result.isValid) {
      console.log("Validation errors:", result.errors);
      setFormErrors(result.errors);
      return;
    }

    console.log("Personal data:", result.data);
    handleNextStep();
  };

  return (
    <div className="flex flex-col gap-4 md:gap-4 w-full p-2">
      <h2 className="md:text-headline3 text-headline4 font-nunito text-secondary-90 font-bold">
        Bank Details
      </h2>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleNext();
        }}
        className="flex flex-col gap-3 w-full"
      >
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="Account Holder Name"
            placeholder="Enter account holder name"
            value={formData?.bankDetails?.accountHolderName || ""}
            onChange={(val) => handleChange("accountHolderName", val)}
            error={getErrorMessage("accountHolderName")}
            disabled={isDisabled}
          />
          <InputField
            label="Bank Name"
            type="text"
            placeholder="Enter Bank Name"
            value={formData?.bankDetails?.bankName || ""}
            onChange={(val) => handleChange("bankName", val)}
            error={getErrorMessage("bankName")}
            disabled={isDisabled}
          />
        </div>
        <div className="flex flex-col md:flex-row gap-2 md:gap-4 items-start">
          <InputField
            label="Account Number"
            type="number"
            placeholder="Enter Account Number"
            value={formData?.bankDetails?.accountNumber || ""}
            onChange={(val) => handleChange("accountNumber", val)}
            error={getErrorMessage("accountNumber")}
            disabled={isDisabled}
          />
          <InputField
            label="Confirm Account Number"
            type="number"
            placeholder="Re-enter account number"
            value={
              isDisabled
                ? formData?.bankDetails?.accountNumber || ""
                : confirmAccountNumber || ""
            }
            onChange={(val) => handleConfirmAccountNumberChange(val)}
            error={getErrorMessage("confirmAccountNumber")}
            disabled={isDisabled}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4 items-start w-full">
          <InputField
            label="IFSC Code"
            type="text"
            placeholder="Enter IFSC Code"
            value={formData?.bankDetails?.ifscCode || ""}
            onChange={(val) => handleChange("ifscCode", val)}
            className="w-full"
            error={getErrorMessage("ifscCode")}
            disabled={isDisabled}
          />
          <RadioInput
            label="Account Type"
            name="AccountType"
            selectedValue={formData?.bankDetails?.accountType || ""}
            onChange={(value) => handleChange("accountType", value)}
            options={[
              { label: "Savings", value: "Saving" },
              { label: "Current", value: "Current" },
            ]}
            className="w-fit"
            error={getErrorMessage("accountType")}
            disabled={isDisabled}
          />
          <div className="w-full flex gap-4">
            <UploadInput
              label="Cancelled Cheque Image"
              name="cancelledChequeImage"
              placeholder="Upload cancelled cheque photo."
              value={formData?.cancelledCheque || null}
              onChange={(val) => handleChange("cancelledCheque", val)}
              note="Note: File type should only be png, jpeg and should be in color"
              noteClassName="text-caption md:text-body1 pr-2"
              spanClassName="text-caption md:text-body1"
              error={getErrorMessage("cancelledCheque")}
              disabled={isDisabled}
            />
          </div>
        </div>
      </form>

      <div className="flex flex-row gap-1 md:gap-2 justify-between sm:justify-end mt-8 mr-2">
        <Button size="sm" variant="outline" onClick={previous}>
          Previous
        </Button>
        <Button
          size="sm"
          variant="default"
          onClick={handleNext}
          icon={IoArrowForward}
        >
          {isDisabled ? "Next" : "Save And Next"}
        </Button>
      </div>
    </div>
  );
};

export default BankDetails;
